/* eslint-disable react/prop-types */
import React from "react";
import { Button } from "../ui/Button";
import { TextareaComponent } from "../ui/Textarea";
import { Trash2, Send, Plus, Minus } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alertDialog/alertDialog";

import { toast } from "react-hot-toast";
import { getPrinterService } from "../../services/printer/MockPrinterService.js";

export const MyOrdersTable = ({
  orderItems,
  selectedTable,
  onRemoveItem,
  onUpdateComment,
  getTotal,
  onQuantityChange,
  onOrderSuccess,
  onSendOrder,
}) => {
  if (!orderItems.length) return null;
  const printerService = getPrinterService();

  const printOrderByDestination = async (orderData) => {
    console.log("printOrderDestination ", orderData);
    try {
      const kitchenItems = orderData.items.filter(
        (item) => item.destination === "Cocina"
      );
      const barItems = orderData.items.filter(
        (item) => item.destination === "Bebidas"
      );

      if (kitchenItems.length > 0) {
        await printerService.printOrder(
          orderData,
          "COMANDA COCINA",
          kitchenItems
        );
      }

      if (barItems.length > 0) {
        await printerService.printOrder(orderData, "COMANDA BAR", barItems);
      }
    } catch (error) {
      console.error("Error al imprimir comandas:", error);
      toast.success("Imprimiendo comanda");
    }
  };

  return (
    <div className="mt-8 animate-fade-in">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-orange-500">
          MIS PEDIDOS
          {selectedTable && (
            <span className="ml-2 px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm">
              Mesa: {selectedTable.description}
            </span>
          )}
        </h2>
        <span className="text-sm text-gray-500">
          {orderItems.length} producto(s)
        </span>
      </div>

      <div className="space-y-4">
        {orderItems.map((item) => (
          <div
            key={item.id}
            className="relative bg-white rounded-lg p-4 shadow-sm border border-orange-100 transition-all duration-200 hover:shadow-md"
          >
            <div className="flex justify-between items-start mb-2">
              <div className="flex-1">
                <div className="flex flex-col">
                  <h3 className="font-semibold text-lg">{item.product.name}</h3>
                  {item.selectedAddon && (
                    <span className="text-sm text-orange-500 mt-1">
                      Opción: {item.selectedAddon}
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-4 mt-2">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() =>
                        onQuantityChange(
                          item.product,
                          item.quantity - 1,
                          item.selectedAddon
                        )
                      }
                      className="p-1 rounded-full hover:bg-orange-100 transition-colors"
                    >
                      <Minus className="w-4 h-4 text-orange-500" />
                    </button>
                    <span className="font-medium">{item.quantity}</span>
                    <button
                      onClick={() =>
                        onQuantityChange(
                          item.product,
                          item.quantity + 1,
                          item.selectedAddon
                        )
                      }
                      className="p-1 rounded-full hover:bg-orange-100 transition-colors"
                    >
                      <Plus className="w-4 h-4 text-orange-500" />
                    </button>
                  </div>
                  <span className="text-orange-500 font-medium">
                    € {(item.product.price * item.quantity).toFixed(2)}
                  </span>
                </div>
              </div>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    className="bg-red-100 hover:bg-red-200 text-red-600 rounded-full p-2 transition-colors"
                    variant="ghost"
                  >
                    <Trash2 className="w-5 h-5" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Eliminar producto?</AlertDialogTitle>
                    <AlertDialogDescription>
                      ¿Estás seguro que deseas eliminar {item.product.name}
                      {item.selectedAddon ? ` (${item.selectedAddon})` : ""} del
                      pedido?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => onRemoveItem(item.id)}
                      className="bg-red-500 hover:bg-red-600"
                    >
                      Eliminar
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>

            <TextareaComponent
              placeholder="Agregar comentarios al pedido..."
              value={item.comment || ""}
              onChange={(e) => onUpdateComment(item.id, e.target.value)}
              className="w-full border-orange-200 focus:border-orange-500 min-h-[60px] mt-2 transition-colors"
            />
          </div>
        ))}

        <div className="border-t border-orange-200 pt-4 mt-4">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold">Total</h3>
            <div className="flex items-center space-x-4">
              <span className="text-xl font-bold text-orange-500">
                € {getTotal(orderItems).toFixed(2)}
              </span>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    className="bg-orange-500 hover:bg-orange-600 text-white flex items-center space-x-2 transition-colors"
                    disabled={!selectedTable}
                  >
                    <Send className="w-4 h-4" />
                    <span>Enviar Pedido</span>
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Confirmar pedido</AlertDialogTitle>
                    <AlertDialogDescription>
                      ¿Estás seguro que deseas enviar el pedido para la mesa{" "}
                      {selectedTable?.description}?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={onSendOrder}
                      className="bg-orange-500 hover:bg-orange-600"
                    >
                      Confirmar
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
